import {
  buildDimensionSortKey,
  CORE_CONSTANT,
  dimensionSortKeyOrder,
  getRecordNodesByPath,
  type ViewConfigKpiParameter,
  type ViewQueryField,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { SortableListWrapper } from '@salescore/frontend-common'
import { Button, Empty, Space } from 'antd'
import { Array, Order, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { useState } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'

import { kpiParameterAtom } from '../../../recoil/navigation/atoms'
import { useRecordsImpl } from '../../../recoil/records'
import { additionalConfigAtom } from '../../../recoil/records/atoms'
import { getLabeledFieldName } from '../../../state/util'

// eslint-disable-next-line sonarjs/todo-tag
// TODO: DimensionFilterと共通化
// eslint-disable-next-line sonarjs/prefer-read-only-props
export function DimensionSorter({ field, hide }: { hide: () => void; field: ViewQueryField }) {
  const records = useRecordsImpl()
  const [kpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const dimension = getDimension(field, kpiPivotParameter.pivot)
  const setAdditionalConfig = useSetRecoilState(additionalConfigAtom)

  const getOptions = (records: ViewQueryRecordNode[]) => {
    const flattenRecords = records.flatMap((x) => getRecordNodesByPath(x, field.nodePath))
    const options = pipe(
      flattenRecords,
      Array.map((x) => {
        const value = x.attributes[field.name]
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const label = x.attributes[getLabeledFieldName(field.name)] as string
        if (value === undefined) {
          // eslint-disable-next-line array-callback-return
          return
        }
        return {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          value: value as string,
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          label: label ?? value,
        }
      }),
      Array.filter(isNotNullish),
      Array.dedupeWith((a, b) => a.value === b.value),
      // 合計行と「-」(分析軸の値が入っていないレコードのまとまり) は表側で位置を固定しているため、並び替えの対象にしない
      Array.filter(
        (x) => x.value !== CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING && x.value !== CORE_CONSTANT.KPI_PIVOT_NULL_STRING,
      ),
    )

    const sortedValues = dimension?.sortedValues
    if (sortedValues === undefined || sortedValues.length === 0) {
      return options
    }
    return pipe(
      options,
      Array.sortBy(Order.mapInput(dimensionSortKeyOrder, (x) => buildDimensionSortKey(sortedValues, x.value, x.label))),
    )
  }

  const options = getOptions(records)

  if (dimension === undefined) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO
    return <>error</>
  }

  return (
    <Body
      options={options}
      onFinish={(values) => {
        setAdditionalConfig((oldValue) => ({
          ...oldValue,
          kpiParameter: {
            ...oldValue.kpiParameter,
            pivot: {
              rows: (oldValue.kpiParameter?.pivot.rows ?? []).map((d) => {
                if (d.key !== dimension.key) {
                  return d
                }
                return {
                  ...d,
                  sortedValues: values,
                }
              }),
              columns: (oldValue.kpiParameter?.pivot.columns ?? []).map((d) => {
                if (d.key !== dimension.key) {
                  return d
                }
                return {
                  ...d,
                  sortedValues: values,
                }
              }),
            },
          },
        }))
        hide()
      }}
      cancel={() => {
        hide()
      }}
      clear={() => {
        setAdditionalConfig((oldValue) => ({
          ...oldValue,
          kpiParameter: {
            ...oldValue.kpiParameter,
            pivot: {
              rows: (oldValue.kpiParameter?.pivot.rows ?? []).map((x) => ({ ...x, sortedValues: undefined })),
              columns: (oldValue.kpiParameter?.pivot.columns ?? []).map((x) => ({ ...x, sortedValues: undefined })),
            },
          },
        }))
        hide()
      }}
    />
  )
}

function getDimension(field: ViewQueryField, pivot: ViewConfigKpiParameter['pivot']) {
  // eslint-disable-next-line sonarjs/todo-tag
  const rowIndex = field.nodePath.length - 1 // TODO: こんなんでいいのか…？？
  const row = pivot.rows[rowIndex]
  return row
}

interface FilterDropdownProperties {
  options: Array<{ value: string; label: string }>
  onFinish: (keys: string[]) => void
  cancel: () => void
  clear: () => void
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function Body({ options, onFinish, cancel, clear }: FilterDropdownProperties) {
  const [items, setItems] = useState(options.map((x) => ({ ...x, key: x.value, disableDrag: false })))

  if (options.length === 0) {
    return (
      <Empty
        description={
          <div>
            {t(`レコードがありません。`)}
            <br />
            <Button
              className="my-3"
              onClick={() => {
                clear()
              }}
            >
              {t(`クリア`)}
            </Button>
          </div>
        }
      />
    )
  }

  return (
    <div
      className="p-2"
      style={{
        maxWidth: 400,
        overflowX: 'auto',
      }}
    >
      <div
        style={{
          maxHeight: 254,
          overflow: 'auto',
        }}
      >
        <SortableListWrapper
          options={{
            bordered: false,
            deletable: false,
          }}
          items={items}
          onChange={(items) => {
            setItems(
              items.map((x) => ({
                value: x.value,
                key: x.key,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
                label: x.label as string, // TODO
                disableDrag: false,
              })),
            )
          }}
        />
      </div>
      <hr className="m-0 my-1 opacity-20" />
      <Space className="mt-2 flex justify-end">
        <Button
          onClick={() => {
            clear()
          }}
          type="text"
          size="small"
        >
          {t(`リセット`)}
        </Button>
        <Button
          onClick={() => {
            onFinish(items.map((x) => x.value))
          }}
          type="primary"
          size="small"
        >
          {t(`適用`)}
        </Button>
      </Space>
    </div>
  )
}
